import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { 
    FaChartLine,
    FaChartBar,
    FaChartArea,
    FaChartPie,
    FaGoogle,
    FaBitcoin,
    FaCopy
} from 'react-icons/fa';
import { FaCirclePlus } from 'react-icons/fa6';
import { MdTrendingUp } from 'react-icons/md';
import { PRIMARY_COLOR, PALE_WHITE_4, PALE_WHITE_1, WHITE, PRIMARY_COLOR_4, GREEN } from '../../appearance/Colors';
import customAnalytics from '../../util/custom-analytics';
import { AuthContext } from '../../util/context';

const FINANCIAL_PLATFORMS = [
    // Copy option - First Row
    {
        id: 'copy',
        name: 'Copy',
        Icon: FaCopy,
        color: PRIMARY_COLOR_4,
        backgroundColor: PALE_WHITE_1,
        isCopy: true
    },
    // Major Financial Platforms - Row 1
    { 
        id: 'yahoo', 
        name: 'Yahoo Finance', 
        Icon: FaChartLine,
        color: '#6001D2'
    },
    { 
        id: 'tradingview', 
        name: 'TradingView', 
        Icon: FaChartBar,
        color: '#2962FF' 
    },
    { 
        id: 'marketwatch', 
        name: 'MarketWatch', 
        Icon: FaChartArea,
        color: '#00C805' 
    },
    // Analysis Platforms - Row 2
    { 
        id: 'finviz', 
        name: 'Finviz', 
        Icon: FaChartArea,
        color: '#E33E2B' 
    },
    { 
        id: 'seekingalpha', 
        name: 'Seeking Alpha', 
        Icon: MdTrendingUp,
        color: '#FC6E3C' 
    },
    { 
        id: 'bloomberg', 
        name: 'Bloomberg', 
        Icon: FaChartLine,
        color: '#000000' 
    },
    // Additional Platforms - Row 3
    { 
        id: 'investing', 
        name: 'Investing.com', 
        Icon: FaChartPie,
        color: '#0D7044' 
    },
    { 
        id: 'nasdaq', 
        name: 'Nasdaq', 
        Icon: FaChartBar,
        color: '#0E6BA4' 
    },
    { 
        id: 'google', 
        name: 'Google Finance', 
        Icon: FaGoogle,
        color: '#4285F4' 
    },
    // Crypto Platforms - Row 4
    { 
        id: 'coinmarketcap', 
        name: 'CoinMarketCap', 
        Icon: FaBitcoin,
        color: '#17181B',
        isCrypto: true
    },
    { 
        id: 'coingecko', 
        name: 'CoinGecko', 
        Icon: FaBitcoin,
        color: '#8DC63F',
        isCrypto: true
    },
    { 
        id: 'binance', 
        name: 'Binance', 
        Icon: FaChartLine,
        color: '#F3BA2F',
        isCrypto: true
    },
    // Request More Button
    { 
        id: 'request', 
        name: 'Request More', 
        Icon: FaCirclePlus,
        color: PRIMARY_COLOR_4,
        backgroundColor: PALE_WHITE_1,
        isRequest: true
    },
];

const customStyles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
    },
    content: {
        position: 'relative',
        top: 'auto',
        left: 'auto',
        right: 'auto',
        bottom: 'auto',
        maxWidth: '600px',
        width: '90%',
        padding: 0,
        border: 'none',
        background: 'none',
        maxHeight: '80vh',
        zIndex: 10000
    }
};

const StockTickerModal = ({ isOpen, onClose, symbol, onSelectPlatform }) => {
    const {t: lnT} = useTranslation();

    const { context: { setShowPaperclip } } = React.useContext(AuthContext);
    const [copiedNetworkId, setCopiedNetworkId] = useState(null);

    const handlePlatformClick = (e, platform) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (platform.isCopy) {
            navigator.clipboard.writeText(symbol).then(() => {
                setCopiedNetworkId(platform.id);
                setTimeout(() => {
                    setCopiedNetworkId(null);
                }, 1500);
                customAnalytics.message_action({
                    "action": "Copy",
                    "type": "StockTicker"
                });
            });
            return;
        }

        if (platform.isRequest) {
            customAnalytics.message_action({
                "action": "Request",
                "type": "Financial Platform"
            });
            setShowPaperclip(true);
            onClose();
            return;
        }

        const fullTicker = symbol.replace('$', '');
        // Handle exchange prefixes like NYSE:T and indices like ^GSPC
        const isIndex = fullTicker.startsWith('^');
        const [exchange, tickerWithSuffix] = isIndex ? [null, fullTicker] : fullTicker.split(':');
        const ticker = tickerWithSuffix || fullTicker;
        const [baseTicker, quoteCurrency] = ticker.split('-');
        const isCurrencyPair = !!quoteCurrency;
        let url;

        switch (platform.id) {
            case 'yahoo':
                url = `https://finance.yahoo.com/quote/${ticker}`;
                break;
            case 'tradingview':
                if (isCurrencyPair) {
                    url = `https://www.tradingview.com/chart/?symbol=${baseTicker}${quoteCurrency}`;
                } else if (isIndex) {
                    // Handle indices differently in TradingView
                    const indexMap = {
                        '^GSPC': 'SPX',
                        '^DJI': 'DJI',
                        '^IXIC': 'IXIC',
                        '^RUT': 'RUT'
                    };
                    const tvSymbol = indexMap[ticker] || ticker.replace('^', '');
                    url = `https://www.tradingview.com/symbols/${tvSymbol}/`;
                } else {
                    // If we have an exchange, use it in the TradingView URL
                    const tvExchange = exchange || 'NYSE';
                    url = `https://www.tradingview.com/symbols/${tvExchange}-${ticker}/`;
                }
                break;
            case 'marketwatch':
                if (isCurrencyPair) {
                    url = `https://www.marketwatch.com/investing/cryptocurrency/${baseTicker.toLowerCase()}`;
                } else if (isIndex) {
                    // Handle indices differently in MarketWatch
                    const indexMap = {
                        '^GSPC': 'SPX',
                        '^DJI': 'DJIA',
                        '^IXIC': 'COMP'
                    };
                    const mwSymbol = indexMap[ticker] || ticker.replace('^', '');
                    url = `https://www.marketwatch.com/investing/index/${mwSymbol.toLowerCase()}`;
                } else {
                    url = `https://www.marketwatch.com/investing/stock/${ticker}`;
                }
                break;
            case 'finviz':
                if (isIndex) {
                    const finvizSymbol = ticker.replace('^', '');
                    url = `https://finviz.com/quote.ashx?t=${finvizSymbol}`;
                } else {
                    url = `https://finviz.com/quote.ashx?t=${baseTicker}`;
                }
                break;
            case 'seekingalpha':
                if (isIndex) {
                    const saSymbol = ticker.replace('^', '.INX');
                    url = `https://seekingalpha.com/symbol/${saSymbol}`;
                } else {
                    url = `https://seekingalpha.com/symbol/${baseTicker}`;
                }
                break;
            case 'bloomberg':
                if (isCurrencyPair) {
                    url = `https://www.bloomberg.com/quote/${baseTicker}:${quoteCurrency}`;
                } else if (isIndex) {
                    // Handle indices differently in Bloomberg
                    const indexMap = {
                        '^GSPC': 'SPX:IND',
                        '^DJI': 'INDU:IND',
                        '^IXIC': 'CCMP:IND'
                    };
                    const bloombergSymbol = indexMap[ticker] || `${ticker.replace('^', '')}:IND`;
                    url = `https://www.bloomberg.com/quote/${bloombergSymbol}`;
                } else {
                    // If we have an exchange, use it in the Bloomberg URL
                    url = `https://www.bloomberg.com/quote/${exchange ? `${ticker}:${exchange}` : ticker}`;
                }
                break;
            case 'investing':
                if (isCurrencyPair) {
                    url = `https://www.investing.com/crypto/${baseTicker.toLowerCase()}/${quoteCurrency.toLowerCase()}`;
                } else if (isIndex) {
                    // Handle indices differently in Investing.com
                    const indexMap = {
                        '^GSPC': 'us-spx-500',
                        '^DJI': 'us-30',
                        '^IXIC': 'nasdaq-composite'
                    };
                    const invSymbol = indexMap[ticker] || ticker.replace('^', '').toLowerCase();
                    url = `https://www.investing.com/indices/${invSymbol}`;
                } else {
                    url = `https://www.investing.com/equities/${ticker.toLowerCase()}`;
                }
                break;
            case 'nasdaq':
                if (isIndex) {
                    const nasdaqSymbol = ticker.replace('^', '');
                    url = `https://www.nasdaq.com/market-activity/index/${nasdaqSymbol}`;
                } else {
                    url = `https://www.nasdaq.com/market-activity/stocks/${baseTicker}`;
                }
                break;
            case 'google':
                // Google Finance supports indices directly
                url = `https://www.google.com/finance/quote/${fullTicker}`;
                break;
            case 'coinmarketcap':
                if (isCurrencyPair) {
                    url = `https://coinmarketcap.com/currencies/${baseTicker.toLowerCase()}`;
                } else {
                    url = `https://coinmarketcap.com/currencies/${ticker.toLowerCase()}`;
                }
                break;
            case 'coingecko':
                if (isCurrencyPair) {
                    url = `https://www.coingecko.com/en/coins/${baseTicker.toLowerCase()}`;
                } else {
                    url = `https://www.coingecko.com/en/coins/${ticker.toLowerCase()}`;
                }
                break;
            case 'binance':
                if (isCurrencyPair) {
                    url = `https://www.binance.com/en/trade/${baseTicker}_${quoteCurrency}`;
                } else {
                    url = `https://www.binance.com/en/trade/${ticker}_USDT`;
                }
                break;
        }

        if (url) {
            window.open(url, '_blank');
            customAnalytics.message_action({
                "action": "View Stock",
                "platform": platform.id,
                "isCrypto": platform.isCrypto || isCurrencyPair,
                "isIndex": isIndex,
                "exchange": exchange || undefined,
                "url": url
            });
        }
    };

    const handlePlatformLongClick = (e, platform) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!platform.isRequest) {
            const fullTicker = symbol.replace('$', '');
            const [baseTicker, quoteCurrency] = fullTicker.split('-');
            const isCurrencyPair = !!quoteCurrency;
            let url;

            switch (platform.id) {
                case 'yahoo':
                    url = `https://finance.yahoo.com/quote/${fullTicker}`;
                    break;
                case 'tradingview':
                    if (isCurrencyPair) {
                        url = `https://www.tradingview.com/chart/?symbol=${baseTicker}${quoteCurrency}`;
                    } else {
                        url = `https://www.tradingview.com/symbols/${fullTicker}/`;
                    }
                    break;
                case 'marketwatch':
                    if (isCurrencyPair) {
                        url = `https://www.marketwatch.com/investing/cryptocurrency/${baseTicker.toLowerCase()}`;
                    } else {
                        url = `https://www.marketwatch.com/investing/stock/${fullTicker}`;
                    }
                    break;
                case 'finviz':
                    url = `https://finviz.com/quote.ashx?t=${baseTicker}`;
                    break;
                case 'seekingalpha':
                    url = `https://seekingalpha.com/symbol/${baseTicker}`;
                    break;
                case 'bloomberg':
                    if (isCurrencyPair) {
                        url = `https://www.bloomberg.com/quote/${baseTicker}:${quoteCurrency}`;
                    } else {
                        url = `https://www.bloomberg.com/quote/${fullTicker}`;
                    }
                    break;
                case 'investing':
                    if (isCurrencyPair) {
                        url = `https://www.investing.com/crypto/${baseTicker.toLowerCase()}/${quoteCurrency.toLowerCase()}`;
                    } else {
                        url = `https://www.investing.com/equities/${fullTicker}`;
                    }
                    break;
                case 'nasdaq':
                    url = `https://www.nasdaq.com/market-activity/stocks/${baseTicker}`;
                    break;
                case 'google':
                    url = `https://www.google.com/finance/quote/${fullTicker}`;
                    break;
                case 'coinmarketcap':
                    if (isCurrencyPair) {
                        url = `https://coinmarketcap.com/currencies/${baseTicker.toLowerCase()}`;
                    } else {
                        url = `https://coinmarketcap.com/currencies/${fullTicker.toLowerCase()}`;
                    }
                    break;
                case 'coingecko':
                    if (isCurrencyPair) {
                        url = `https://www.coingecko.com/en/coins/${baseTicker.toLowerCase()}`;
                    } else {
                        url = `https://www.coingecko.com/en/coins/${fullTicker.toLowerCase()}`;
                    }
                    break;
                case 'binance':
                    if (isCurrencyPair) {
                        url = `https://www.binance.com/en/trade/${baseTicker}_${quoteCurrency}`;
                    } else {
                        url = `https://www.binance.com/en/trade/${fullTicker}_USDT`;
                    }
                    break;
            }

            if (url) {
                window.open(url, '_blank');
                customAnalytics.message_action({
                    "action": "View Stock",
                    "platform": platform.id,
                    "isCrypto": platform.isCrypto || isCurrencyPair,
                    "url": url
                });
            }
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={customStyles}
            contentLabel={lnT('messages:stockTickerModal')}
            shouldCloseOnOverlayClick={true}
            onClick={(e) => e.stopPropagation()}
        >
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <Header>
                    <HeaderText>{lnT('common:view')}{symbol}{lnT('common:on')}</HeaderText>
                    <CloseButton onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}>
                        <IoClose size={20} color={PALE_WHITE_4} />
                    </CloseButton>
                </Header>
                <NetworksGrid onClick={(e) => e.stopPropagation()}>
                    {FINANCIAL_PLATFORMS.map((platform) => (
                        <NetworkItem
                            key={platform.id}
                            onClick={(e) => handlePlatformClick(e, platform)}
                            onContextMenu={(e) => handlePlatformLongClick(e, platform)}
                        >
                            <IconContainer style={{ 
                                backgroundColor: copiedNetworkId === platform.id ? GREEN : (platform.backgroundColor || platform.color)
                            }}>
                                <platform.Icon size={28} color={WHITE} />
                            </IconContainer>
                            <NetworkName style={{
                                color: copiedNetworkId === platform.id ? GREEN : PALE_WHITE_4
                            }}>
                                {platform.isCopy && copiedNetworkId === platform.id ? lnT('messages:copied_8d525e5f') : platform.name}
                            </NetworkName>
                        </NetworkItem>
                    ))}
                </NetworksGrid>
            </ModalContent>
        </Modal>
    );
};

const ModalContent = styled.div`
    background-color: ${PRIMARY_COLOR};
    border-radius: 16px;
    overflow: hidden;
    max-height: 80vh;
`;

const Header = styled.div`
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const HeaderText = styled.span`
    color: ${PALE_WHITE_4};
    font-weight: 600;
    text-transform: uppercase;
    font-size: 14px;
`;

const CloseButton = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 16px;
    background-color: ${PALE_WHITE_1};
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    padding: 0;
`;

const NetworksGrid = styled.div`
    padding: 2px 16px 16px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 16px;
    max-height: calc(80vh - 64px);
    overflow-y: auto;

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
        display: none;
    }
    
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
`;

const NetworkItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
        transform: translateY(-2px);
    }
`;

const IconContainer = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 6px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: relative;
    transition: all 0.2s ease;
`;

const NetworkName = styled.span`
    color: ${PALE_WHITE_4};
    font-size: 12px;
    text-align: center;
    font-weight: 500;
    transition: all 0.2s ease;
`;

export default StockTickerModal; 